import { useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { z } from 'zod';

import type {
  OrderItemsModule, OrderItemsModuleApi, ReviewClosingBreakdownLine, ReviewSummaryLine,
} from '../../OrderForm/types';
import SteelItemsSlot from './components/SteelItemsSlot';
import { SteelItemsProvider, useSteelItems } from './contexts/SteelItemsContext';
import { computeItemPendencies } from './Hooks/useItemPricePendencies';
import { useOrderCalculations } from './Hooks/useOrderCalculations';
import { itemOrderSchema, OrderSchemaType } from './order.schema';
import { buildSteelItemsPayload, mapSteelItems, SteelItemPayload } from './steelItems.payload';
import { RateioItemInput, rateioSellingPrice } from '../../OrderForm/utils/rateioSellingPrice';
import { computeReviewTotals, formatCurrency } from '../../OrderForm/utils/reviewTotals';
import { buildSteelBreakdown, buildSteelClosingRows } from './utils/steelClosingRows';

const ITEM_TYPE_LABELS: Record<string, string> = {
  MATERIAL: 'Matéria Prima',
  PRODUCT: 'Produto Final',
  RESOURCE: 'Insumo',
  SERVICE: 'Serviço',
};

const useSteelItemsModule = (): OrderItemsModuleApi<OrderSchemaType, SteelItemPayload> => {
  const { watch, getValues, setValue, reset } = useFormContext<OrderSchemaType>();
  const { setHasValidationError, setNoSavedItemsType, toggleItem } = useSteelItems();

  const items = watch('items') || [];
  const calculations = useOrderCalculations();

  // referência estável: é dependência do efeito que registra o save no header, e esse efeito
  // faz setState acima na árvore — recriar a função aqui realimenta o render em loop
  const buildItemsPayload = useCallback(
    () => buildSteelItemsPayload(getValues('items')),
    [getValues],
  );

  const validateBeforeSave = useCallback(() => {
    const mapped = mapSteelItems(getValues('items'));
    const saved = mapped.filter((item) => item.isSaved === true);
    if (saved.length > 0) {
      setHasValidationError(false);
      return { ok: true as const };
    }

    setHasValidationError(true);
    const code = mapped.length === 0 ? 'no-items' as const : 'not-saved' as const;
    setNoSavedItemsType(code);
    return {
      ok: false as const,
      code,
      message: code === 'no-items'
        ? 'O pedido deve ter pelo menos um item.'
        : 'Há itens que ainda não foram salvos.',
    };
  }, [getValues, setHasValidationError, setNoSavedItemsType]);

  const distributePrice = useCallback((total: number) => {
    const currentItems = getValues('items') || [];
    const input: RateioItemInput[] = currentItems.map((it) => ({
      itemPrice: it.itemPrice ?? null,
      sellingPrice: it.priceDetails?.sellingPrice ?? null,
    }));
    rateioSellingPrice(total, input).forEach((res, index) => {
      if (res.rateado) {
        setValue(`items.${index}.priceDetails.sellingPrice`, res.sellingPrice ?? undefined, {
          shouldDirty: true,
          shouldValidate: false,
        });
      }
    });
  }, [getValues, setValue]);

  const entries = items.map((it, index: number) => {
    const nome = it?.nomeCustomizado || ITEM_TYPE_LABELS[it?.itemType as string] || 'Item';
    const locked = computeItemPendencies(it).length > 0;
    const efetivo = it?.priceDetails?.sellingPrice ?? it?.itemPrice;
    return {
      index,
      label: `${index + 1} · ${nome}`,
      status: locked ? '⚠ pendências' : (efetivo != null && Number(efetivo) > 0 ? formatCurrency(Number(efetivo)) : '—'),
      locked,
    };
  });

  const closingRows = buildSteelClosingRows(items);
  const closingBreakdown = useCallback(
    (index: number): ReviewClosingBreakdownLine[] => buildSteelBreakdown((getValues('items') || [])[index]),
    [getValues],
  );

  const review = computeReviewTotals(closingRows);
  const reviewSummary: ReviewSummaryLine[] = [
    { label: 'Itens do pedido', emphasis: true },
    { label: 'Itens:', value: String(review.count) },
    { label: 'Peças:', value: review.pieces > 0 ? String(review.pieces) : '—' },
    { label: 'Total Cadastrado:', value: review.registeredTotal > 0 ? formatCurrency(review.registeredTotal) : '—' },
    { label: 'Total de Venda:', value: review.effectiveTotal > 0 ? formatCurrency(review.effectiveTotal) : '—' },
  ];

  return {
    totals: {
      quantity: calculations.totalQuantity,
      pieces: calculations.totalQuantity,
      subtotal: calculations.total,
      taxes: calculations.taxes,
      practicedTotal: calculations.practicedTotal,
      canDistribute: true,
    },
    reviewSummary,
    closingRows,
    closingBreakdown,
    navigation: {
      itemsLabel: `2 · Itens (${items.filter((it) => it.isSaved).length}) ▾`,
      entries,
      onSelectItem: (index: number) => toggleItem(index, true),
    },
    buildItemsPayload,
    validateBeforeSave,
    hydrate: (order: OrderSchemaType) => reset(order),
    distributePrice,
  };
};

export const steelItemsModule: OrderItemsModule<OrderSchemaType, SteelItemPayload> = {
  itemsSchema: z.array(itemOrderSchema),
  useModule: useSteelItemsModule,
  Slot: SteelItemsSlot,
  Provider: SteelItemsProvider,
};
